/* eslint-disable no-loop-func */
import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSpring, animated } from 'react-spring';
import _ from 'lodash';
import { delay, playSound, windowSize, scrollClass, dbget, dbdel, dbset } from '../../HOME/utils';
import * as f from '../logic';
import '../styles.css';
import MessageBox from '../../HOME/COMPONENTS/MessageBox';
import { useInterval } from '../../HOME/utils';
import Tile from './Tile';
import Spot from './Spot';
import { NoGradientBackground } from './Background';
import { IN_PILE, IN_TRAY, IN_PLAY, TO_PILE, TO_TRAY, PN_BLUE, PN_INDIGO, INVALID_ACTION_PIECES_IN_TRAY } from '../const';
import { PN_SHAPES, PN_COLORS, INVALID_ACTION_NO_MATCH, INVALID_ACTION_PAIRS, INVALID_ACTION_ORPHAN } from '../const';
import CheckIcon from '../../HOME/SVG/CheckIcon';
import { SET_STARTED_TRUE, SET_STARTED_FALSE, HIDE_PILE } from '../REDUX/actions';
import { UNSELECT_TILE, UPDATE_IN_PILE_COUNT, SET_MAX_PAIRS, SET_PAIRS, UPDATE_STATS } from '../REDUX/actions';
import { REPORT_INVALID_ACTION, UNREPORT_INVALID_ACTION, SET_TURN, SET_OPP_PAIRS, SELECT_TILE } from '../REDUX/actions';
import { SET_SCORE, SET_MAX_SCORE, SET_OVER_TRUE, SET_OVER_FALSE } from '../REDUX/actions';
import { GREEN, MODE_AI, MODE_SOLO } from '../../HOME/const';
import { SHOW_LEADERS, OVER_RESTART } from '../../HOME/REDUX/actions';
import { REQUEST_RESET_STATS_OFF, REQUEST_SURRENDER_OFF, SET_RESTART_OFF, SET_RESTART_ON } from '../../HOME/REDUX/actions';
import Pile from './Pile';
import GameOverOptions from '../../HOME/COMPONENTS/GameOverOptions';

const Board = () => {
  const XXS = false;
  const ONE_AT_A_TIME = false;

  const dispatch = useDispatch();
  const [_dummy, setDummy] = useState(false);
  const { pn, common } = useSelector(state => state);
  const { restart } = common;
  const { mode } = pn;
  const ai = mode === MODE_AI;
  const solo = mode === MODE_SOLO;
  let [l, setLocal] = useState({ shapes: [], colors: [], spots: [], tiles: [] });
  const [progress, setProgress] = useState(false);
  const progressBox = useRef(null);
  const [blocked, setBlocked] = useState(0);
  const traySize = ONE_AT_A_TIME ? 1 : pn.size === 1 ? 3 : pn.size < 4 ? 4 : 5;

  const forceUpdate = () => setDummy(!_dummy);
  const tilesInPile = () => _.filter(l.tiles, tile => tile.status === IN_PILE);
  const tilesToPile = () => _.filter(l.tiles, tile => tile.status === TO_PILE);
  const tilesInTray = () => _.filter(l.tiles, tile => tile.status === IN_TRAY || tile.status === TO_TRAY);
  const tilesInPlay = () => _.filter(l.tiles, tile => tile.status === IN_PLAY);
  const maxSecs = () => pn.maxPairs * 10;
  const handleDismissProgress = () => delay(() => setProgress(false));

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  useEffect(() => {
    window.addEventListener('resize', forceUpdate);
    l.mounted = true;

    if (restart) {
      l.restart = true;
      setStats(ai ? 'lost' : 'draw');
    } else {
      load();
    }

    dispatch(SET_RESTART_OFF);
    dispatch(HIDE_PILE);
    dispatch(UNREPORT_INVALID_ACTION);

    return () => {
      window.removeEventListener('resize', forceUpdate);
      l.mounted = false;

      dispatch(REQUEST_RESET_STATS_OFF);
      dispatch(REQUEST_SURRENDER_OFF);
      dispatch(UNSELECT_TILE);
    };
  }, [restart, dispatch]);

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const load = () => {
    dbget(f.boardKey(pn), json => {
      if (json) {
        doLoad(json);
      } else {
        doInit();
      }
    });
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const doLoad = json => {
    const { shapes, colors, secs, tiles, spots, pairs, maxPairs, oppPairs, turn } = JSON.parse(json);
    l = { shapes, colors, tiles, spots, over: false };

    dispatch(SET_OVER_FALSE);
    dispatch({ type: SET_PAIRS, count: (pn.pairs = pairs) });
    dispatch({ type: SET_MAX_PAIRS, count: (pn.maxPairs = maxPairs) });

    pn.started = tilesInPlay().length > 0;

    if (solo) {
      l.secs = secs;
      setScores();
    } else {
      dispatch({ type: SET_OPP_PAIRS, count: (pn.oppPairs = oppPairs) });
      dispatch({ type: SET_TURN, turn: (pn.turn = turn || 1) });

      if (pn.started) {
        dispatch(SET_STARTED_TRUE);
      }
    }

    setLocal(l);

    if (pn.started) {
      setProgress(true);

      delay(() => {
        progressBox.current && progressBox.current.click();

        if (ai && pn.turn === 2) {
          aiMove();
        }
      }, 1000);
    }

    delay(updateInPileCount);
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const selectShapesAndColors = () => {
    const combos = [
      XXS ? [{ s: 2, c: 1 }] : [{ s: 2, c: 3 }, { s: 3, c: 2 }],
      [{ s: 3, c: 3 }],
      [{ s: 3, c: 4 }, { s: 4, c: 3 }],
      [{ s: 3, c: 5 }, { s: 4, c: 4 }, { s: 5, c: 3 }],
      [{ s: 4, c: 5 }, { s: 5, c: 4 }],
    ][pn.size - 1];

    const combo = combos[_.random(combos.length - 1)];

    l.shapes.splice(0, l.shapes.length, ..._.sampleSize(PN_SHAPES, combo.s));

    do {
      l.colors.splice(0, l.colors.length, ..._.sampleSize(PN_COLORS, combo.c));
    } while (_.includes(l.colors, PN_BLUE) && _.includes(l.colors, PN_INDIGO));
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const doInit = () => {
    playSound(common, 'dice');
    dispatch(UNSELECT_TILE);

    selectShapesAndColors();

    _.remove(l.tiles);
    l.spots = [{ row: 1, col: 1 }];

    l.over = false;
    dispatch(SET_OVER_FALSE);

    dispatch({ type: SET_PAIRS, count: (pn.pairs = 0) });
    dispatch({ type: SET_MAX_PAIRS, count: (pn.maxPairs = f.maxPairs(l.shapes.length, l.colors.length)) });

    if (solo) {
      l.secs = maxSecs();
      setScores();
    } else {
      let whoStarts = pn.options.whoStarts || 1;

      if (!l.restart || whoStarts < 3) {
        if (whoStarts === 3) {
          whoStarts = pn.whoStarted ? 3 - pn.whoStarted : 1;
        }

        dispatch({ type: SET_TURN, turn: whoStarts });
      }

      dispatch(SET_STARTED_FALSE);
      dispatch({ type: SET_OPP_PAIRS, count: (pn.oppPairs = 0) });
    }

    l.restart = false;

    let id = 0;

    _.each(l.shapes, s => {
      _.each(l.colors, c => {
        for (let i = 0; i < 3; i++) {
          l.tiles.push({ id: ++id, shape: s, color: c, status: IN_PILE });
        }
      });
    });

    refillTray();
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const handleTapToStart = () => {
    aiMove();
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const handleConfirmSurrender = yes => {
    delay(() => {
      dispatch(REQUEST_SURRENDER_OFF);

      if (!yes) {
        return;
      }

      dispatch(SET_RESTART_ON);
    });
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const handleConfirmResetStats = yes => {
    if (yes) {
      dispatch({ type: UPDATE_STATS });
      dbdel(f.statsKey(pn));
    }

    delay(() => dispatch(REQUEST_RESET_STATS_OFF));
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const calcScore = pairs => {
    const { maxPairs } = pn;
    let score = pairs * (pairs - 1.0) + pairs * (1 + l.secs / maxSecs());

    const maxScore = maxPairs * (maxPairs + 1);
    score = _.round((1000 * score) / maxScore);

    return score;
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const setScores = () => {
    let score = calcScore(pn.maxPairs);

    if (score !== pn.maxScore) {
      dispatch({ type: SET_MAX_SCORE, score });
    }

    score = calcScore(pn.pairs);

    if (score !== pn.score) {
      dispatch({ type: SET_SCORE, score: (pn.score = score) });
    }
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const setStats = outcome => {
    dbdel(f.boardKey(pn));

    let { plays, aggScore, bestScore, aggPairs, bestPairs, wins, draws } = pn.stats || {};
    plays = (plays || 0) + 1;

    let stats;

    if (solo) {
      aggScore = (aggScore || 0) + pn.score;
      const aveScore = _.round(aggScore / plays);
      bestScore = bestScore ? Math.max(pn.score, bestScore) : pn.score;

      stats = { plays, aveScore, aggScore, bestScore };
    } else {
      if (outcome === 'won') {
        wins = (wins || 0) + 1;
      } else if (outcome === 'draw') {
        draws = (draws || 0) + 1;
      }

      stats = { plays, wins, draws };

      if (ai) {
        aggPairs = (aggPairs || 0) + pn.pairs;
        const avePairs = _.round(aggPairs / plays);
        bestPairs = bestPairs ? Math.max(pn.pairs, bestPairs) : pn.pairs;

        stats = { ...stats, avePairs, aggPairs, bestPairs };
      }
    }

    dbset(f.statsKey(pn), stats);
    dispatch({ type: UPDATE_STATS, stats });
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  useInterval(
    () => {
      if (l.secs > 0) {
        --l.secs;
        setScores();
        save();
      }
    },
    solo && l.secs && !l.over ? 1000 : null
  );

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const toJson = () => {
    const ob = { shapes: l.shapes, colors: l.colors, tiles: l.tiles, spots: l.spots, pairs: pn.pairs, maxPairs: pn.maxPairs };
    const incl = ['shapes', 'colors', 'secs', 'tiles', 'spots', 'pairs', 'maxPairs', 'id', 'row', 'col', 'shape', 'color', 'status', 'invalid'];

    if (solo) {
      ob.secs = l.secs;
    } else {
      ob.turn = pn.turn;
      ob.oppPairs = pn.oppPairs;

      incl.push('oppPairs');
      incl.push('turn');
    }

    const json = JSON.stringify(ob, incl);
    return json;
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const save = () => {
    if (!_.find(l.tiles, tile => tile.status === TO_TRAY)) {
      dbset(f.boardKey(pn), toJson());
    }
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const winnerInfo = () => {
    let sound = 'draw';
    let message = "IT'S A DRAW!";
    let outcome = 'draw';

    if (solo) {
      message = 'GAME OVER';
      sound = pn.pairs < pn.maxPairs ? 'draw' : 'won';
    } else if (ai) {
      if (pn.pairs > pn.oppPairs) {
        message = 'YOU WON!';
        sound = 'won';
        outcome = 'won';
      } else if (pn.pairs < pn.oppPairs) {
        message = 'YOU LOST';
        sound = 'lost';
        outcome = 'lost';
      }
    } else if (pn.pairs > pn.oppPairs) {
      message = 'PLAYER 1 WON!';
      sound = 'player1wins';
      outcome = 'won';
    } else if (pn.pairs < pn.oppPairs) {
      message = 'PLAYER 2 WON!';
      sound = 'player2wins';
      outcome = 'lost';
    }

    return { sound, message, outcome };
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const onOver = () => {
    l.over = true;

    if (solo) {
      setScores();
    }

    const { outcome, sound } = winnerInfo();
    setStats(outcome);

    delay(() => {
      playSound(common, sound);
      dispatch(SET_OVER_TRUE);

      if (!solo) {
        dispatch({ type: SET_TURN, turn: 0 });
      }
    }, 300);
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const updateInPileCount = () => {
    const count = tilesInPile().length + tilesToPile().length;
    dispatch({ type: UPDATE_IN_PILE_COUNT, count });
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const refillTray = () => {
    let tiles = tilesInTray();

    _.each(tiles, tile => {
      tile.status = TO_PILE;
    });

    if (_.isEmpty(tilesInPile()) && _.isEmpty(tilesToPile())) {
      l.over = true;
      delay(onOver);
      return;
    }

    if (_.isEmpty(tiles)) {
      doRefill();
    } else {
      forceUpdate();
    }
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const doRefill = () => {
    Promise.resolve().then(updateInPileCount);

    if (!_.isEmpty(tilesInTray())) {
      return false;
    }

    const tiles = _.sampleSize(tilesInPile().concat(tilesToPile()), traySize);

    _.each(tiles, (tile, i) => {
      tile.row = -1;
      tile.col = i * 2 + 1;
      tile.status = TO_TRAY;
    });

    return true;
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const havePlayablePiecesInTray = () => {
    const inPlayTiles = tilesInPlay();
    let canPair = _.isEmpty(inPlayTiles);

    if (canPair) {
      return true;
    }

    const inTrayTiles = tilesInTray();

    _.each(inTrayTiles, tile => {
      _.each(l.spots, spot => {
        const neighbors = f.getNeighbors(spot);

        _.each(neighbors, ({ row: r, col: c }) => {
          const t = f.find(inPlayTiles, r, c);

          if (t && f.isValidPair(tile, t)) {
            canPair = true;
            return false;
          }
        });

        if (canPair) {
          return false;
        }
      });

      if (canPair) {
        return false;
      }
    });

    return canPair;
  };

  useInterval(() => setBlocked(0), blocked ? 1600 : null);

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const handleDone = initOnly => {
    if (common.resetStats || common.surrender) {
      return;
    }

    if (blocked) {
      return;
    }

    setBlocked(blocked + 1);

    if (l.over) {
      doInit();
      return;
    }

    if (initOnly) {
      return;
    }

    if (pn.selected) {
      dispatch(UNSELECT_TILE);
    }

    if (solo) {
      const canPair = havePlayablePiecesInTray();

      if (canPair) {
        dispatch({ type: REPORT_INVALID_ACTION, message: INVALID_ACTION_PIECES_IN_TRAY });
        return;
      }
    }

    refillTray();

    if (!solo) {
      dispatch({ type: SET_TURN, turn: (pn.turn = 3 - pn.turn) });
    }
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const handleCheck = () => {
    if (ai) {
      if (pn.turn === 2) {
        return;
      }

      if (!pn.started) {
        dispatch(SET_STARTED_TRUE);
      }
    }

    handleDone();

    if (!l.over && ai && pn.turn === 2) {
      delay(aiMove, 1600);
    }
  };

  const handleGameOver = op => {
    if (op === SHOW_LEADERS) {
      dispatch({ type: SHOW_LEADERS });
    } else if (op === OVER_RESTART) {
      handleDone(true);
    }
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const placeTile = (tile, spot) => {
    playSound(common, 'cluck');

    const { row, col } = spot;
    const fromTray = tile.status === IN_TRAY;

    let tiles = tilesInPlay();
    let loss = 0;
    let gain = 0;

    if (!fromTray) {
      const neighbors = f.getNeighbors(tile);

      _.each(neighbors, ({ row: r, col: c }) => {
        if (f.find(tiles, r, c)) {
          ++loss;
        }
      });
    }

    tile.status = IN_PLAY;
    tile.row = row;
    tile.col = col;
    tile.spot = null;

    const neighbors = f.getNeighbors(tile);

    _.each(neighbors, ({ row: r, col: c }) => {
      if (f.find(tiles, r, c)) {
        ++gain;
      }
    });

    gain -= loss;

    if (gain) {
      playSound(common, gain > 2 ? 'coins' : gain > 1 ? 'coin2' : 'coin1');
    }

    if (solo || pn.turn === 1) {
      dispatch({ type: SET_PAIRS, count: (pn.pairs += gain) });
    } else {
      dispatch({ type: SET_OPP_PAIRS, count: (pn.oppPairs += gain) });
    }

    l.over = pn.pairs + (pn.oppPairs || 0) === pn.maxPairs;

    if (l.over) {
      onOver();
    }

    tiles = tilesInPlay();
    let spots = [];

    if (!l.over) {
      const addSpot = (r, c) => spots.push({ row: r, col: c });

      const moveFromTray = () => {
        spots = _.filter(l.spots, s => s.row !== row || s.col !== col);
        let neighbors = f.getNeighbors(spot);

        _.each(neighbors, ({ row: r, col: c }) => {
          if (f.find(spots.concat(tiles), r, c)) {
            return;
          }

          addSpot(r, c);

          if (r === 0) {
            shiftRows = 1;
          }

          if (c === 0) {
            shiftCols = 1;
          }
        });
      };

      const reposition = () => {
        _.remove(l.spots);

        _.each(tiles, tile => {
          const neighbors = f.getNeighbors(tile);

          _.each(neighbors, ({ row: r, col: c }) => {
            if (!f.find(spots.concat(tiles), r, c)) {
              addSpot(r, c);
            }
          });
        });

        const minRow = _.minBy(spots, s => s.row).row;

        if (minRow !== 1) {
          shiftRows = 1 - minRow;
        }

        const minCol = _.minBy(spots, s => s.col).col;

        if (minCol !== 1) {
          shiftCols = 1 - minCol;
        }
      };

      let shiftRows = 0;
      let shiftCols = 0;

      if (fromTray) {
        moveFromTray();
      } else {
        reposition();
      }

      if (shiftRows || shiftCols) {
        _.each(spots.concat(tiles), ob => {
          if (shiftRows) {
            ob.row += shiftRows;
          }
          if (shiftCols) {
            ob.col += shiftCols;
          }
        });
      }
    }

    l.spots = spots;
    l.tiles = _.sortBy(l.tiles, ['row', 'col']);

    if (!l.over) {
      save();
    }

    forceUpdate();
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const selectFromTray = any => {
    const inPlayTiles = tilesInPlay();
    const inTrayTiles = _.shuffle(tilesInTray());

    if (_.isEmpty(inPlayTiles)) {
      return _.isEmpty(inTrayTiles) ? { bestTile: null, bestSpot: null } : { bestTile: inTrayTiles[0], bestSpot: l.spots[0] };
    }

    let bestTile = null;
    let bestSpot = null;
    let max = 0;

    _.each(inTrayTiles, tile => {
      _.each(_.shuffle(l.spots), spot => {
        let pairs = 0;
        let goodTile = null;
        let goodSpot = null;
        const neighbors = f.getNeighbors(spot);

        _.each(neighbors, ({ row: r, col: c }) => {
          const t = f.find(inPlayTiles, r, c);

          if (!t) {
            return;
          }

          if (!f.isValidPair(tile, t)) {
            pairs = 0;
            goodTile = null;
            goodSpot = null;
            return false;
          }

          if (any && goodSpot) {
            return;
          }

          ++pairs;
          goodTile = tile;
          goodSpot = spot;
        });

        if (goodSpot) {
          if (any) {
            bestTile = goodTile;
            bestSpot = goodSpot;
            return false;
          }

          if (pairs > max) {
            max = pairs;
            bestTile = goodTile;
            bestSpot = goodSpot;
          }
        }
      });

      if (any && bestSpot) {
        return false;
      }
    });

    return { bestTile, bestSpot };
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const selectToReposition = () => {
    const inPlayTiles = tilesInPlay();

    let foundTile = null;
    let foundSpot = null;

    _.each(inPlayTiles, tile => {
      const neighbors = f.getNeighbors(tile);
      let countFrom = 0;

      _.each(neighbors, ({ row: r, col: c }) => {
        if (f.find(inPlayTiles, r, c)) {
          ++countFrom;
        }
      });

      if (countFrom !== 1) {
        return;
      }

      _.each(l.spots, spot => {
        const neighbs = f.getNeighbors(spot);
        let countTo = 0;

        _.each(neighbs, ({ row: r, col: c }) => {
          const tob = f.find(inPlayTiles, r, c);

          if (!tob) {
            return;
          }

          if (f.isValidPair(tile, tob, true)) {
            ++countTo;
          } else {
            countTo = 0;
            return false;
          }
        });

        if (countTo > countFrom) {
          foundTile = tile;
          foundSpot = spot;
          return false;
        }
      });

      if (foundTile) {
        return false;
      }
    });

    return { from: foundTile, to: foundSpot };
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const aiMove = () => {
    if (l.mounted === false) {
      return;
    }

    let tile, spot;

    if (pn.skill === 3) {
      let { from, to } = selectToReposition();

      if (from && to) {
        tile = from;
        spot = to;
      }
    }

    if (!tile) {
      const { bestTile, bestSpot } = selectFromTray(pn.skill === 1);
      tile = bestTile;
      spot = bestSpot;
    }

    const selectAndMove = () => {
      playSound(common, 'click');
      dispatch({ type: SELECT_TILE, selected: (pn.selected = tile) });

      const doMove = () => {
        pn.selected.spot = spot;
        dispatch(UNSELECT_TILE);
        delay(aiMove, 1000);
      };

      delay(() => {
        if (f.scrollTo(spot)) {
          delay(doMove, 600); // wait for the scroll
        } else {
          doMove();
        }
      }, 1000);
    };

    if (tile && spot) {
      if (!pn.started) {
        dispatch(SET_STARTED_TRUE);
      }

      if (tile.row > 0 && f.scrollTo(tile)) {
        delay(selectAndMove, 600); // wait for the scroll
      } else {
        selectAndMove();
      }
    } else if (!l.over) {
      delay(handleDone);
    }
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const validateSpots = () => {
    _.each(l.spots, spot => {
      spot.invalid = l.over;
    });

    if (!pn.selected) {
      return;
    }

    const selected = pn.selected;
    const fromTray = selected.status === IN_TRAY;
    const inPlayTiles = tilesInPlay();
    let breakPairs = 0;
    const selectedNeighborTiles = [];

    if (!fromTray) {
      const neighbors = f.getNeighbors(selected);

      _.each(neighbors, ({ row: r, col: c }) => {
        const tile = f.find(inPlayTiles, r, c);

        if (tile) {
          selectedNeighborTiles.push(tile);
          ++breakPairs;
        }
      });
    }

    _.each(l.spots, spot => {
      const neighbors = f.getNeighbors(spot);
      let pairs = 0;

      _.each(neighbors, ({ row: r, col: c }) => {
        const tile = f.find(inPlayTiles, r, c);

        if (tile && tile !== selected) {
          if (!f.isValidPair(tile, selected)) {
            spot.invalid = INVALID_ACTION_NO_MATCH;
            return false;
          }

          ++pairs;
        }
      });

      if (!spot.invalid && pairs < breakPairs) {
        spot.invalid = INVALID_ACTION_PAIRS;
      }

      _.each(selectedNeighborTiles, tile => {
        const neighbs = f.getNeighbors(tile);

        if (f.find(neighbs, spot.row, spot.col)) {
          return;
        }

        if (f.find(neighbs, selected.row, selected.col) && _.filter(neighbs, ({ row, col }) => f.find(inPlayTiles, row, col)).length === 1) {
          spot.invalid = INVALID_ACTION_ORPHAN;
          return false;
        }
      });
    });
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const renderTray = () => {
    const tiles = tilesInTray().concat(tilesToPile());

    return (
      <div className="pn-tray">
        {tiles.length ? (
          <div className={`pn-tray-top pn-tray-top-${traySize}`}>
            {_.map(tiles, tile => {
              const style = { gridArea: `1 / ${tile.col}` };
              return <Tile key={tile.id} over={l.over} tile={tile} style={style} doRefill={doRefill} placeTile={placeTile} save={save} />;
            })}
          </div>
        ) : null}
        <div className="pn-tray-bottom" style={{ backgroundImage: NoGradientBackground }} />
      </div>
    );
  };

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const renderSurface = () => {
    if (!_.find(l.tiles, tile => tile.spot)) {
      validateSpots();
    }

    return (
      <div className={`pn-surface`}>
        <div id="surface" className={scrollClass} style={{ padding: `${l.over ? 10 : 0}px` }}>
          {_.map(l.spots, spot => {
            spot.id = spot.row * 100 + spot.col;
            return <Spot key={spot.id} spot={spot} />;
          })}
          {_.map(tilesInPlay(), tile => {
            return <Tile key={tile.id} over={l.over} tile={tile} placeTile={placeTile} />;
          })}
        </div>
      </div>
    );
  };

  const totalTileCount = l.shapes.length * l.colors.length * 3;
  const showEmptyPrompt = !l.over && totalTileCount && tilesInPlay().length === totalTileCount;

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const pileEmptyStyle = useSpring({
    opacity: showEmptyPrompt ? 1 : 0,
    immediate: !showEmptyPrompt,
  });

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const playAgainStyle = useSpring({
    opacity: pn.over ? 1 : 0,
    zIndex: pn.over ? 1 : 0,
    immediate: !pn.over,
  });

  const { x: wx, y: wy } = windowSize();
  const checkSize = 44;
  const surrenderMessage = ai && !pn.over ? 'Surrender?' : 'Start over?';
  const tapToStart = ai && pn.turn === 2 && !pn.started && !common.resetStats && !common.surrender;

  //••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  return (
    <>
      <div className="pn-board" style={{ height: `${wy - 148}px` }}>
        {showEmptyPrompt && (
          <animated.span className="pn-tray-message" style={pileEmptyStyle}>
            DRAW PILE IS EMPTY
          </animated.span>
        )}
        {pn.over && (
          <animated.span className="pn-tray-message" style={playAgainStyle} onClick={() => handleDone(true)}>
            {winnerInfo().message}
          </animated.span>
        )}
        {renderTray()}
        {renderSurface()}
        {pn.message && (
          <div className="pn-invalid-message-screen" style={{ width: wx }} onClick={() => dispatch(UNREPORT_INVALID_ACTION)}>
            <div className="pn-invalid-message pn-message">{pn.message}</div>
          </div>
        )}
        {common.surrender && !l.over && (
          <MessageBox classes="pn-message" message={surrenderMessage} yesNo width={wx} onReply={handleConfirmSurrender} />
        )}
        {common.resetStats && <MessageBox classes="pn-message" message="Reset stats?" yesNo width={wx} onReply={handleConfirmResetStats} />}
        {progress && !tapToStart && (
          <MessageBox classes="pn-message" ref={progressBox} message="GAME IN PROGRESS" onReply={handleDismissProgress} />
        )}
        {tapToStart && <MessageBox classes="pn-message" message="Tap to start" onReply={handleTapToStart} />}
        <span
          className={`toolbar-button ${blocked || (ai && pn.turn === 2) ? 'toolbar-button-disabled' : ''} pn-check`}
          style={{ height: checkSize, left: `${(wx - checkSize) / 2}px` }}
          onClick={handleCheck}>
          <CheckIcon width={checkSize} color={showEmptyPrompt ? 'orangered' : GREEN} />
        </span>
        {pn.over && !pn.resetStats && <GameOverOptions handleResponse={handleGameOver} style={{ gridArea: '2/1' }} />}
      </div>
      {pn.showPile && <Pile tiles={tilesInPile()} shapes={l.shapes} colors={l.colors} />}
    </>
  );
};

export default Board;
